import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import type { ReactElement } from 'react';

export type LazyRoute = { path: string; component: ReactElement };

export const MAX_RETRIES = 2;
export const RETRY_DELAY = 1000;
const MAX_RELOAD_ATTEMPTS = 2;
const RELOAD_STORAGE_KEY = 'chunk_error_reload_count';

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const reloadAfterChunkError = () => {
  const count = parseInt(sessionStorage.getItem(RELOAD_STORAGE_KEY) || '0', 10);
  if (count >= MAX_RELOAD_ATTEMPTS) {
    sessionStorage.removeItem(RELOAD_STORAGE_KEY);
    return; // Stop reloading, user will see the error toast
  }
  sessionStorage.setItem(RELOAD_STORAGE_KEY, String(count + 1));
  window.location.reload();
};

export const isChunkError = (err: unknown): boolean => {
  if (!(err instanceof Error)) return false;
  const msg = err.message.toLowerCase();
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk')
  );
};

type ImportFn = () => Promise<{ lazyRoutes: LazyRoute[] }>;
const defaultImport: ImportFn = () => import('./lazyRoutes');

export const useLazyRoutes = (importFn: ImportFn = defaultImport) => {
  const [lazyRoutes, setLazyRoutes] = useState<LazyRoute[]>([]);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    (async () => {
      let lastError: Error | null = null;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (!mounted) return;
        try {
          const { lazyRoutes: routes } = await importFn();
          if (mounted) {
            sessionStorage.removeItem(RELOAD_STORAGE_KEY);
            setLazyRoutes(routes);
          }
          return;
        } catch (err) {
          lastError = err instanceof Error ? err : new Error(String(err));
          if (!isChunkError(lastError)) {
            break;
          }
          if (attempt < MAX_RETRIES) {
            await delay(RETRY_DELAY);
          }
        }
      }

      // All retries failed
      if (mounted && lastError) {
        if (isChunkError(lastError)) {
          toast.error(
            'Something went wrong loading the page. Click to reload.',
            {
              autoClose: false,
              onClick: reloadAfterChunkError,
            }
          );
        } else {
          setError(lastError);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  if (error) throw error;
  return lazyRoutes;
};
